import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { DragDropUpload } from "@/components/DragDropUpload";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import { FUEL_TYPES, TRANSMISSIONS, BODY_TYPES, COLORS } from "@/lib/format";
import {
  fetchMakes,
  fetchModels,
  fetchEngines,
  fetchTrims,
  decodeVin,
  resolveCanonicalMake,
  resolveCanonicalModel,
} from "@/lib/carApi";
import {
  importListingFromUrl,
  parseImportSource,
  parseImportUrl,
  readImportClipboard,
} from "@/lib/listingImport";
import { enrichFilledFeatures } from "@/lib/featureCoalesce";
import {
  applyTrimSpecs,
  applyVinDecode,
  applyImportFilled,
  describeImportFilled,
  CONDITIONS,
  defaultListingTitle,
  describeVinFilled,
  emptyExtendedFields,
  extendedFieldsFromCar,
  isValidVin,
  vinMeetsListingRules,
  listingStep1Blockers,
  MONTH_OPTIONS,
  pickExtendedPayload,
} from "@/lib/listingForm";
import { ListingVehicleFields } from "@/components/ListingVehicleFields";
import { ListingFormSection } from "@/components/ListingFormSection";
import { ArrowLeft, ArrowRight, Loader2, Check, Car as CarIcon, Images, Coins, AlertCircle, Sparkles, RotateCw, Wand2, TrendingUp, ChevronsUp, Megaphone, Share2, Link2 } from "lucide-react";
import { splitDefaultListingAddons, sanitizeListingAddonKeys } from "@/lib/listingDefaultAddons";
import { toast } from "sonner";
import api from "@/lib/api";
import { Section360Video } from "@/components/Section360Video";
import { normalize360Frames } from "@/lib/viewer360Frames";
import { PremiumOnboarding } from "@/components/PremiumOnboarding";
import { AiBgPresetSelector, AI_BG_PRESET_KEY, bumpAiBgPresetUsage } from "@/components/AiBgPresetSelector";
import { AiBgComparison } from "@/components/AiBgComparison";
import { ListingRequirements, allRequiredAccepted } from "@/components/ListingRequirements";
import { CarFeaturePicker } from "@/components/CarFeaturePicker";
import { countSelectedFeatures, sanitizeFeatureKeys } from "@/lib/carFeatures";
import { HUNGARY_COUNTIES } from "@/lib/hungaryCountyPaths";
import { getAddonInfo } from "@/lib/creditAddons";
import { AddonInfoButton } from "@/components/AddonInfoButton";
import {
  AiSupportBriefEditor,
  normalizeAiSupportBrief,
  aiSupportBriefValid,
  aiSupportBriefToPayload,
} from "@/components/AiSupportBriefEditor";
import { isServiceAvailable, serviceUnavailableMessage } from "@/lib/serviceAvailability";
import { runAiBgOnCarImages } from "@/lib/aiBgRun";
import { AiBgRerunSection } from "@/components/AiBgRerunSection";

const REPEATABLE_ADDON_KEYS = new Set(["facebook_crosspost"]);

const ADDON_META = {
  viewer_360: { icon: RotateCw },
  ai_photo_bg: { icon: Wand2 },
  ai_customer_support: { icon: Sparkles },
  facebook_crosspost: { icon: Share2, repeatable: true },
};

const WEEKLY_ADDON_META = {
  list_boost: { icon: TrendingUp },
  priority_ranking: { icon: ChevronsUp },
  meta_ads: { icon: Megaphone },
};

function buildCarPayload(form, images, { images360 = [], video360 = null, editingCar = null, addons = {}, aiSupportPrivate = null } = {}) {
  const viewer360Active = addons.viewer_360 || editingCar?.addon_viewer_360;
  const title = (form.listing_title || "").trim() || defaultListingTitle(form);
  return {
    make: form.make,
    model: form.model,
    year: Number(form.year),
    price: Number(form.price),
    fuel: form.fuel,
    transmission: form.transmission,
    engine: form.engine,
    mileage: Number(form.mileage),
    body_type: form.body_type,
    color: form.color || null,
    description: form.description,
    features: form.features || [],
    county: form.county,
    city: form.city,
    address: form.address?.trim() || null,
    listing_title: title || null,
    image: images[0],
    images,
    images_360: viewer360Active ? images360 : [],
    video_360: viewer360Active && video360 ? video360 : null,
    max_days: Math.max(1, Math.min(365, Number(form.max_days) || 30)),
    ...pickExtendedPayload({ ...form, listing_title: title }),
    ...(aiSupportPrivate ? { ai_support_private: aiSupportPrivate } : {}),
  };
}

function buildInitialForm(car) {
  const rawMake = car?.make || "";
  const canonMake = rawMake ? resolveCanonicalMake(rawMake) || "" : "";
  const rawModel = car?.model || "";
  const canonModel = canonMake && rawModel ? resolveCanonicalModel(canonMake, rawModel) || "" : "";
  const core = car
    ? {
        make: canonMake,
        model: canonModel,
        year: car.year ?? 2020,
        price: car.price ?? 5000000,
        fuel: car.fuel || "Benzin",
        transmission: car.transmission || "Manuális",
        engine: car.engine || "",
        horsepower: car.horsepower ?? null,
        acceleration: car.acceleration ?? null,
        mileage: car.mileage ?? 50000,
        body_type: car.body_type || "Ferdehátú",
        color: car.color || "",
        description: car.description || "",
        max_days: car.max_days || 30,
        features: Array.isArray(car.features) ? car.features : [],
        county: car.county || "",
        city: car.city || "",
        address: car.address || car.viewing_address || "",
      }
    : {
        make: "",
        model: "",
        year: 2020,
        price: 5000000,
        fuel: "Benzin",
        transmission: "Manuális",
        engine: "",
        horsepower: null,
        acceleration: null,
        mileage: 50000,
        body_type: "Ferdehátú",
        color: "",
        description: "",
        max_days: 30,
        features: [],
        county: "",
        city: "",
        address: "",
      };
  return { ...emptyExtendedFields(), ...extendedFieldsFromCar(car), ...core };
}

export default function NewListing() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const editingCar = location.state?.car || null;
  const focusAiSupport = !!location.state?.focusAiSupport;
  /** Szerkesztés: navigációs state gyakran hiányos — /my/cars-ból frissítjük */
  const [editCar, setEditCar] = useState(editingCar);

  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => buildInitialForm(editingCar));
  const defaultsAppliedRef = useRef(false);
  const [images, setImages] = useState(editingCar?.images?.length ? editingCar.images : (editingCar?.image ? [editingCar.image] : []));
  const [images360, setImages360] = useState(() => normalize360Frames(editingCar?.images_360));
  const [video360Url, setVideo360Url] = useState(editingCar?.video_360 || "");
  const [addons, setAddons] = useState({
    viewer_360: !!editingCar?.addon_viewer_360,
    ai_photo_bg: !!editingCar?.addon_ai_photo_bg,
    ai_customer_support: !!editingCar?.addon_ai_customer_support,
    facebook_crosspost: false,
  });
  const aiBgPreset = AI_BG_PRESET_KEY;
  const [weeklyAddons, setWeeklyAddons] = useState({ list_boost: false, priority_ranking: false });
  const [savedCar, setSavedCar] = useState(null);
  const [processingAI, setProcessingAI] = useState(false);
  const [aibgCompare, setAibgCompare] = useState(null); // {originals, processed, preset}
  const [aibgProgress, setAibgProgress] = useState(null); // {done, total, preset}
  const aibgCancelRef = useRef(false);
  const [aiSupportPrivate, setAiSupportPrivate] = useState(() =>
    normalizeAiSupportBrief(editingCar?.ai_support_private)
  );
  const [aiSupportSetupOpen, setAiSupportSetupOpen] = useState(focusAiSupport);
  const aiBriefSectionRef = useRef(null);

  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [trims, setTrims] = useState([]);
  const [loadingTrims, setLoadingTrims] = useState(false);
  const [vinLoading, setVinLoading] = useState(false);
  const [importLink, setImportLink] = useState("");
  const [importAdvancedPaste, setImportAdvancedPaste] = useState("");
  const [importLoading, setImportLoading] = useState(false);
  const [importImages, setImportImages] = useState(true);
  const [engines, setEngines] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(true);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingEngines, setLoadingEngines] = useState(false);
  const [acceptedReq, setAcceptedReq] = useState({});

  const listingRequirements = config?.listing_requirements || [];
  const vinRequired = !!config?.listing_vin_required;
  const toggleReq = (id) => setAcceptedReq((prev) => ({ ...prev, [id]: !prev[id] }));
  const requirementsOk = !!editingCar || allRequiredAccepted(listingRequirements, acceptedReq);

  useEffect(() => {
    api.get("/pricing-config").then((r) => setConfig(r.data)).catch(() => {});
    refreshUser().catch(() => {});
    api
      .get("/import-listing-url/health")
      .then((r) => {
        const v = r.data?.listing_import_version;
        if (v != null && v < 2) {
          toast.message("A hirdetés-import API régi verzió — deploy / API restart szükséges.");
        } else if (r.data && r.data.curl_cffi === false) {
          toast.message("A szerveren hiányzik a curl_cffi — a hasznaltauto.hu import nem fog működni.");
        }
      })
      .catch(() => {});
  }, []);

  // Szerkesztés: friss adat a szerverről (cím, megye, extrák — a navigációs state nem mindig teljes)
  useEffect(() => {
    if (!editingCar?.id) return;
    let cancelled = false;
    api
      .get("/my/cars")
      .then((r) => {
        if (cancelled) return;
        const fresh = (r.data || []).find((c) => c.id === editingCar.id);
        if (!fresh) return;
        setEditCar(fresh);
        setForm(buildInitialForm(fresh));
        setImages((prev) => {
          const fromServer =
            Array.isArray(fresh.images) && fresh.images.length
              ? fresh.images
              : fresh.image
                ? [fresh.image]
                : [];
          if (!fromServer.length) return prev;
          if (!prev.length || fromServer.length >= prev.length) return fromServer;
          return prev;
        });
        setAddons({
          viewer_360: !!fresh.addon_viewer_360,
          ai_photo_bg: !!fresh.addon_ai_photo_bg,
          ai_customer_support: !!fresh.addon_ai_customer_support,
          facebook_crosspost: false,
        });
        setImages360(normalize360Frames(fresh.images_360));
        setVideo360Url(fresh.video_360 || "");
        setAiSupportPrivate(normalizeAiSupportBrief(fresh.ai_support_private));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [editingCar?.id]);

  useEffect(() => {
    if (!focusAiSupport) return;
    setStep(2);
    setAiSupportSetupOpen(true);
    toast.message("Töltsd ki az AI asszisztens briefet, majd kapcsold be a kiegészítőt.");
    const t = window.setTimeout(() => {
      aiBriefSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => window.clearTimeout(t);
  }, [focusAiSupport]);

  useEffect(() => {
    if (editingCar || defaultsAppliedRef.current) return;
    const { oneTime, weekly } = splitDefaultListingAddons(user?.default_listing_addons);
    if (!oneTime.length && !weekly.length) return;
    if (!config) return;
    defaultsAppliedRef.current = true;
    if (oneTime.length) {
      setAddons((prev) => {
        const next = { ...prev };
        for (const k of oneTime) {
          if (isServiceAvailable(config, k)) next[k] = true;
        }
        return next;
      });
    }
    if (weekly.length) {
      setWeeklyAddons((prev) => {
        const next = { ...prev };
        for (const k of weekly) {
          if (isServiceAvailable(config, k)) next[k] = true;
        }
        return next;
      });
    }
  }, [editingCar, user?.default_listing_addons, config]);

  useEffect(() => {
    let cancelled = false;
    setLoadingMakes(true);
    fetchMakes()
      .then((list) => {
        if (cancelled) return;
        setMakes(list);
      })
      .finally(() => !cancelled && setLoadingMakes(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!form.make) { setModels([]); return; }
    let cancelled = false;
    setLoadingModels(true);
    fetchModels(form.make)
      .then((list) => {
        if (cancelled) return;
        setModels(list);
        const canon = resolveCanonicalModel(form.make, form.model);
        if (canon && canon !== form.model) {
          setForm((prev) => ({ ...prev, model: canon }));
        }
      })
      .finally(() => !cancelled && setLoadingModels(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.make]);

  useEffect(() => {
    if (!form.make || !form.model) {
      setEngines([]);
      return;
    }
    let cancelled = false;
    setLoadingEngines(true);
    fetchEngines(form.make, form.model)
      .then((list) => {
        if (cancelled) return;
        const withCurrent = form.engine && !list.includes(form.engine) ? [form.engine, ...list] : list;
        setEngines(withCurrent);
      })
      .finally(() => !cancelled && setLoadingEngines(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.make, form.model]);

  useEffect(() => {
    if (!form.make || !form.model) {
      setTrims([]);
      return;
    }
    let cancelled = false;
    setLoadingTrims(true);
    fetchTrims(form.make, form.model, form.year)
      .then((list) => { if (!cancelled) setTrims(list); })
      .finally(() => !cancelled && setLoadingTrims(false));
    return () => { cancelled = true; };
  }, [form.make, form.model, form.year]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const setMake = (v) => {
    const canon = resolveCanonicalMake(v);
    if (!canon) return;
    setForm((prev) => ({
      ...prev,
      make: canon,
      model: "",
      engine: "",
      trim_id: "",
      trim_label: "",
    }));
  };
  const setModel = (v) => {
    const canon = resolveCanonicalModel(form.make, v);
    if (!canon) return;
    setForm((prev) => ({
      ...prev,
      model: canon,
      engine: "",
      trim_id: "",
      trim_label: "",
    }));
  };

  const handleTrimSelect = async (trimId) => {
    if (!trimId) {
      setForm((prev) => ({ ...prev, trim_id: "", trim_label: "" }));
      return;
    }
    const local = trims.find((t) => t.id === trimId);
    try {
      const r = await api.get(`/catalog/trims/${trimId}`);
      const specs = r.data?.specs || {};
      setForm((prev) => applyTrimSpecs({ ...prev, trim_id: trimId }, specs));
    } catch {
      if (local) {
        setForm((prev) => applyTrimSpecs({ ...prev, trim_id: trimId }, { ...local, trim_id: trimId, trim_label: local.label }));
      }
    }
  };

  const handleDecodeVin = async () => {
    const vin = (form.vin || "").trim();
    if (vin.length !== 17) {
      toast.error("Az alvázszám 17 karakter kell legyen.");
      return;
    }
    setVinLoading(true);
    try {
      const data = await decodeVin(vin);
      if (!data.ok) {
        toast.error(data.error || "Nem sikerült dekódolni.");
        if (data.warnings?.length) {
          data.warnings.forEach((w) => toast.message(w));
        }
        return;
      }
      const filled = data.filled || {};
      setForm((prev) => {
        let next = applyVinDecode(prev, data);
        const cm = resolveCanonicalMake(next.make);
        if (cm) {
          next = { ...next, make: cm, model: resolveCanonicalModel(cm, next.model) || "" };
        }
        if (!next.listing_title?.trim()) {
          next.listing_title = defaultListingTitle(next);
        }
        return next;
      });
      const labels = describeVinFilled(filled);
      if (labels.length) {
        toast.success(`Kitöltve: ${labels.join(", ")}. Ellenőrizd a mezőket.`);
      } else {
        toast.message("Csak az alvázszám mentve — további adat nem érkezett.");
      }
      (data.warnings || []).forEach((w) => toast.message(w));
      if (data.suggested_trims?.length === 1) {
        await handleTrimSelect(data.suggested_trims[0].id);
      } else if (data.suggested_trims?.length > 1) {
        toast.message(`Válassz kivitelt a listából (${data.suggested_trims.length} lehetőség).`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "VIN dekódolás sikertelen.");
    } finally {
      setVinLoading(false);
    }
  };

  const applyImportResponse = async (data, url) => {
      if (!data?.ok) {
        toast.error("Az import nem sikerült.");
        return;
      }
      const filled = enrichFilledFeatures(data?.filled || {});
      data.filled = filled;
      setForm((prev) => {
        let next = applyImportFilled(prev, data);
        const cm = resolveCanonicalMake(next.make);
        if (cm) {
          next = { ...next, make: cm, model: resolveCanonicalModel(cm, next.model) || next.model || "" };
        }
        return next;
      });
      if (data.images?.length) {
        setImages((prev) => [...prev, ...data.images]);
        if (step === 1) toast.message(`${data.images.length} kép betöltve — a 2. lépésben ellenőrizd.`);
      }
      const labels = describeImportFilled(filled);
      const featN = Array.isArray(filled.features) ? filled.features.length : 0;
      if (labels.length) {
        toast.success(`Kitöltve: ${labels.join(", ")}. Ellenőrizd az adatokat!`);
        if (!featN) {
          toast.message("Ha kevés felszereltség jött át, használd a „Vágólap beolvasása” lépést (Cmd+A a hirdetésen).");
        }
      } else {
        toast.error(
          "Kevés adat érkezett — nyisd meg a linket, Cmd+A → Cmd+C, majd „Vágólap beolvasása”.",
        );
      }
      (data.warnings || []).forEach((w) => {
        if (labels.length || data.needs_client_capture) toast.message(w);
      });
      if (data.needs_client_capture && url) {
        window.open(url, "_blank", "noopener,noreferrer");
        toast.message("Megnyitottuk a hirdetést új lapon — Cmd+A, Cmd+C, majd „Vágólap beolvasása”.");
      }
  };

  const runListingImport = async ({ url, pageText = "", pageHtml = "" }) => {
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      toast.error("Adj meg egy https:// hirdetés linket (hasznaltauto.hu).");
      return;
    }
    setImportLoading(true);
    try {
      const data = await importListingFromUrl(url, {
        importImages,
        pageText,
        pageHtml,
      });
      await applyImportResponse(data, url);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg || d).join(" ")
            : "Hirdetés import sikertelen.";
      toast.error(msg);
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportListingUrl = async () => {
    const { url: linkUrl } = parseImportUrl(importLink);
    const paste = importAdvancedPaste.trim() ? parseImportSource(importAdvancedPaste) : null;
    const url = linkUrl || paste?.url || "";
    const pageHtml = paste?.pageHtml || "";
    const pageText = paste?.pageText || "";
    await runListingImport({ url, pageText, pageHtml });
  };

  const handleImportFromClipboard = async () => {
    const { url } = parseImportUrl(importLink);
    if (!url.startsWith("http")) {
      toast.error("Előbb add meg a hirdetés linkjét.");
      return;
    }
    try {
      const clip = await readImportClipboard();
      await runListingImport({
        url,
        pageText: clip.pageText,
        pageHtml: clip.pageHtml,
      });
    } catch (e) {
      toast.error(e.message || "Vágólap beolvasás sikertelen.");
    }
  };

  const toggleFeature = (key) => {
    setForm((prev) => {
      const has = prev.features.includes(key);
      return {
        ...prev,
        features: has ? prev.features.filter((f) => f !== key) : [...prev.features, key],
      };
    });
  };
  const featureCount = countSelectedFeatures(form.features);
  const defaultAddonCount = !editingCar
    ? sanitizeListingAddonKeys(user?.default_listing_addons).length
    : 0;

  const step1Blockers = listingStep1Blockers(form, {
    vinRequired,
    resolveMake: resolveCanonicalMake,
    resolveModel: resolveCanonicalModel,
  });
  const step1Valid = step1Blockers.length === 0;

  const uploadCost = config?.listing_upload ?? 20;
  const dailyCost = config?.listing_daily ?? 10;
  const oneTimeCfg = config?.addons_one_time || {};
  const weeklyCfg = config?.addons_weekly || {};
  const selectedOneTime = Object.entries(addons).filter(([, v]) => v).map(([k]) => k);
  const selectedWeekly = Object.entries(weeklyAddons).filter(([, v]) => v).map(([k]) => k);
  const oneTimeCost = selectedOneTime.reduce((s, k) => s + (Number(oneTimeCfg[k]) || 0), 0);
  const weeklyCost = selectedWeekly.reduce((s, k) => s + (Number(weeklyCfg[k]) || 0), 0);
  const totalCost = uploadCost + oneTimeCost;
  const balance = user?.credits ?? 0;
  const insufficient = !editingCar && balance < totalCost;

  const toggleAddon = (key) => {
    setAddons((a) => ({ ...a, [key]: !a[key] }));
  };
  const toggleWeekly = (key) => {
    if (!weeklyAddons[key] && config && !isServiceAvailable(config, key)) {
      toast.error(serviceUnavailableMessage(config, key));
      return;
    }
    setWeeklyAddons((a) => ({ ...a, [key]: !a[key] }));
  };

  const isAddonActive = (key) => {
    if (REPEATABLE_ADDON_KEYS.has(key)) return false;
    return addons[key] || !!editCar?.[`addon_${key}`];
  };

  const has360Addon = isAddonActive("viewer_360");
  const showAiSupportBrief = isAddonActive("ai_customer_support") || aiSupportSetupOpen;

  const handleAddonClick = async (key) => {
    const active = isAddonActive(key);
    if (REPEATABLE_ADDON_KEYS.has(key) && active) return;
    if (!active && config && !isServiceAvailable(config, key)) {
      toast.error(serviceUnavailableMessage(config, key));
      return;
    }
    if (key === "ai_customer_support" && !active) {
      setAiSupportSetupOpen(true);
    }
    if (key === "ai_customer_support" && !editingCar && !addons.ai_customer_support) {
      toggleAddon(key);
      return;
    }
    if (!active && key === "ai_customer_support" && !aiSupportBriefValid(aiSupportPrivate)) {
      toast.error("Előbb töltsd ki lent az AI asszisztens briefet.");
      aiBriefSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (editingCar) {
      if (!active) {
        const cost = Number(oneTimeCfg[key]) || 0;
        if (balance < cost) {
          toast.error(`Nincs elég kredit (${cost} kell)`);
          return;
        }
      }
      try {
        const body = { type: key, enable: !active };
        if (key === "ai_customer_support" && !active) {
          body.ai_support_private = aiSupportBriefToPayload(aiSupportPrivate);
        }
        const r = await api.post(`/my/cars/${editCar?.id || editingCar.id}/addon`, body);
        const fresh = r.data?.car;
        if (REPEATABLE_ADDON_KEYS.has(key)) {
          toast.success("Facebook megosztás kérése elküldve — a csapat hamarosan posztol.");
        } else if (active) {
          setAddons((a) => ({ ...a, [key]: false }));
          setEditCar((c) => (c && fresh ? { ...c, ...fresh, [`addon_${key}`]: false } : c));
          toast.success("Kiegészítő kikapcsolva");
        } else {
          setAddons((a) => ({ ...a, [key]: true }));
          setEditCar((c) => (c ? { ...c, [`addon_${key}`]: true } : c));
          toast.success(`${getAddonInfo(key)?.label || key} aktiválva`);
          if (key === "viewer_360") {
            toast.message("Görges lejjebb — ott veheted fel a 360° videót.");
          }
        }
        refreshUser().catch(() => {});

        if (!active && key === "ai_photo_bg" && images.length > 0) {
          const carId = editCar?.id || editingCar.id;
          const gallerySnapshot = [...images];
          bumpAiBgPresetUsage(aiBgPreset);
          setProcessingAI(true);
          toast.info(`AI háttércsere folyamatban… (${gallerySnapshot.length} kép)`);
          try {
            const result = await runAiBgOnCarImages(carId, gallerySnapshot, aiBgPreset);
            const processed = result.images || images;
            setImages(processed);
            setEditCar((c) => (c ? { ...c, image: processed[0], images: processed, addon_ai_photo_bg: true } : c));
            toast.success(`AI háttér kész — ${result.processed} kép`);
            setAibgCompare({ originals: result.originals, processed, preset: aiBgPreset, carId });
          } catch (e) {
            toast.error(e.response?.data?.detail || e.message || "AI háttércsere sikertelen");
          } finally {
            setProcessingAI(false);
          }
        }
      } catch (e) {
        const detail = e.response?.data?.detail || "";
        if (typeof detail === "string" && detail.includes("már aktív")) {
          setAddons((a) => ({ ...a, [key]: true }));
          setEditCar((c) => (c ? { ...c, [`addon_${key}`]: true } : c));
          toast.message("A 360° kiegészítő már aktiválva van — lent megnyílik a felvétel.");
          return;
        }
        toast.error(detail || "Nem sikerült aktiválni");
      }
      return;
    }
    toggleAddon(key);
  };

  const submit = async () => {
    if (images.length === 0) {
      toast.error("Tölts fel legalább egy képet");
      return;
    }
    const aiAddonOn = isAddonActive("ai_customer_support");
    if (aiAddonOn && !aiSupportBriefValid(aiSupportPrivate)) {
      toast.error("Az AI ügyfélszolgálathoz töltsd ki a privát briefet (legalább elérhetőség vagy megjegyzés).");
      setStep(2);
      return;
    }
    setSaving(true);
    const briefPayload = aiSupportBriefValid(aiSupportPrivate)
      ? aiSupportBriefToPayload(aiSupportPrivate)
      : null;
    const payload = {
      ...buildCarPayload(form, images, { images360, video360: video360Url, editingCar: editCar || editingCar, addons, aiSupportPrivate: briefPayload }),
      selected_addons: editingCar ? undefined : selectedOneTime,
    };
    try {
      if (editingCar) {
        await api.put(`/my/cars/${editingCar.id}`, payload);
        toast.success("Hirdetés frissítve");
        navigate("/fiok");
        return;
      }
      const r = await api.post("/my/cars", payload);
      const created = r.data;
      const parts = [`${uploadCost} kredit feladás`];
      if (oneTimeCost > 0) parts.push(`+${oneTimeCost} kredit kiegészítők`);
      toast.success(`Hirdetés feladva – ${parts.join(", ")} levonva`);
      refreshUser().catch(() => {});

      // Heti kiegészítők: külön addon endpoint, hogy ne zavarjuk a create flow-t
      for (const key of selectedWeekly) {
        try { await api.post(`/my/cars/${created.id}/addon`, { type: key }); }
        catch (e) { toast.error(`${getAddonInfo(key)?.label || key}: ${e.response?.data?.detail || "nem sikerült aktiválni"}`); }
      }

      // AI háttércsere: azonnal indul — minden galéria kép (batch + merge)
      if (selectedOneTime.includes("ai_photo_bg") && images.length > 0) {
        bumpAiBgPresetUsage(aiBgPreset);
        setProcessingAI(true);
        aibgCancelRef.current = false;
        const originalsBefore = [...images];
        const total = originalsBefore.length;
        toast.info(`AI háttércsere folyamatban… (0/${total})`);
        setAibgProgress({ done: 0, total, preset: aiBgPreset });
        try {
          const result = await runAiBgOnCarImages(created.id, originalsBefore, aiBgPreset, {
            onProgress: ({ done, total: t }) => {
              if (!aibgCancelRef.current) {
                setAibgProgress({ done, total: t, preset: aiBgPreset });
              }
            },
          });
          const processedImages = result.images || originalsBefore;
          setImages(processedImages);
          const n = result.processed ?? processedImages.length;
          toast.success(`AI háttér lecserélve ${n}/${total} képen (${aiBgPreset})`);
          setAibgCompare({
            originals: originalsBefore,
            processed: processedImages,
            preset: aiBgPreset,
            carId: created.id,
          });
        } catch (e) {
          toast.error(e.response?.data?.detail || e.message || "AI háttércsere sikertelen");
        } finally {
          setProcessingAI(false);
          setAibgProgress(null);
        }
      }

      // 360° kiegészítő: azonnal nyisd meg a 360 feltöltő felületet a hirdetés mentése után
      if (selectedOneTime.includes("viewer_360")) {
        setSavedCar(created);
        setStep(3);
        setTimeout(() => {
          document.querySelector('[data-testid="listing-360-section"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
        return;
      }
      navigate("/fiok");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mentés sikertelen");
    } finally {
      setSaving(false);
    }
  };

  const saveThreeSixty = async () => {
    if (!savedCar) return;
    if (images360.length < 12) {
      toast.error("Legalább 12 kocka szükséges — vedd fel újra a videót");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/my/cars/${savedCar.id}/360-view`, {
        images_360: images360,
        video_360: video360Url || null,
      });
      toast.success("360° videó mentve");
      navigate("/fiok");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mentés sikertelen");
    } finally { setSaving(false); }
  };

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white">
      <Navbar />
      <section className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate("/fiok")} className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" /> Vissza a fiókhoz
          </button>

          <h1 className="font-heading text-3xl md:text-4xl font-light">
            {editingCar ? "Hirdetés szerkesztése" : "Új hirdetés feladása"}
          </h1>

          {/* Cost preview */}
          {!editingCar && (
            <div data-testid="listing-cost-preview" className={`mt-4 glass-strong rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3 border ${insufficient ? "border-red-400/40" : "border-electric/30"}`}>
              <div className="flex items-center gap-3">
                <Coins className={`w-5 h-5 ${insufficient ? "text-red-400" : "text-electric"}`} strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">Költség</div>
                  <div className="text-sm flex flex-wrap items-center gap-x-1 gap-y-1">
                    <span className="inline-flex items-center gap-1">
                      Egyszeri feladás: <b data-testid="cost-upload">{uploadCost} kredit</b>
                      <AddonInfoButton addonKey="listing_upload" testid="info-listing-upload" />
                    </span>
                    {oneTimeCost > 0 && <> + kiegészítők: <b data-testid="cost-addons">{oneTimeCost} kredit</b> = <b data-testid="cost-total" className="text-electric">{totalCost} kredit</b></>}
                    <span className="inline-flex items-center gap-1">
                      · napi fenntartás: <b data-testid="cost-daily">{dailyCost} kredit</b>
                      <AddonInfoButton addonKey="listing_daily" testid="info-listing-daily" />
                    </span>
                    {weeklyCost > 0 && <> · heti kiegészítők: <b data-testid="cost-weekly">{weeklyCost} kredit / hét</b></>}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400 text-right">Egyenleged</div>
                <div className={`font-heading text-lg ${insufficient ? "text-red-400" : "text-white"}`} data-testid="cost-balance">
                  {balance} kredit
                </div>
              </div>
              {insufficient && (
                <div className="w-full flex items-start gap-2 text-sm text-red-300 border-t border-red-400/20 pt-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Nincs elég krediteged – <a href="/kreditek" className="underline text-red-200">tölts fel</a> a feladáshoz.</span>
                </div>
              )}
            </div>
          )}

          {/* Steps indicator */}
          <div className="flex items-center gap-3 mt-6">
            {[{ n: 1, label: "Adatok", icon: CarIcon }, { n: 2, label: "Képek", icon: Images }].map((s, i) => (
              <React.Fragment key={s.n}>
                <div className={`flex items-center gap-2 ${step >= s.n ? "text-electric" : "text-slate-500"}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border ${step >= s.n ? "border-electric bg-electric/15" : "border-white/15"}`}>
                    {step > s.n ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" strokeWidth={1.5} />}
                  </div>
                  <span className="text-sm">{s.label}</span>
                </div>
                {i === 0 && <div className={`h-px flex-1 ${step > 1 ? "bg-electric" : "bg-white/10"}`} />}
              </React.Fragment>
            ))}
          </div>

          <div className="glass rounded-3xl p-8 mt-6">
            {step === 1 ? (
              <div className="space-y-6">
                <ListingFormSection
                  title="Autó alapadatai"
                  hint={
                    vinRequired
                      ? "Az alvázszám kötelező. VIN dekódolás vagy külső hirdetés linkje is kitöltheti a mezőket."
                      : "Az alvázszám opcionális (pl. hasznaltauto.hu-n gyakran nincs feltüntetve). VIN dekódolás vagy link import kitöltheti a többi mezőt."
                  }
                  testid="listing-section-vehicle"
                >
                  <div className="md:col-span-2 rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
                    <p className="text-sm text-slate-300 flex items-center gap-2">
                      <Link2 className="w-4 h-4 shrink-0 text-electric" />
                      Hasznaltauto.hu: elég a <b>link</b> — először automatikusan letöltjük. Ha kevés adat jön, Cmd+A a hirdetésen, majd <b>Vágólap beolvasása</b> (képek is).
                    </p>
                    <div className="space-y-1">
                      <label className="text-xs text-slate-400" htmlFor="listing-import-link">
                        Hirdetés link
                      </label>
                      <input
                        id="listing-import-link"
                        data-testid="listing-import-link"
                        type="url"
                        value={importLink}
                        onChange={(e) => setImportLink(e.target.value)}
                        placeholder="https://www.hasznaltauto.hu/szemelyauto/…"
                        className="w-full rounded-xl bg-black/30 border border-electric/25 px-3 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:border-electric/50 outline-none font-mono text-[13px]"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:items-center sm:justify-between">
                      <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="listing-import-images"
                          checked={importImages}
                          onChange={(e) => setImportImages(e.target.checked)}
                          className="rounded border-white/20"
                        />
                        Képek importálása
                      </label>
                      <div className="flex flex-wrap gap-2 justify-end">
                        <button
                          type="button"
                          data-testid="listing-import-clipboard"
                          disabled={importLoading || !importLink.trim()}
                          onClick={() => handleImportFromClipboard()}
                          className="shrink-0 px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-sm hover:border-electric/40 disabled:opacity-40"
                        >
                          Vágólap beolvasása
                        </button>
                        <button
                          type="button"
                          data-testid="listing-import-url-submit"
                          disabled={importLoading || !importLink.trim()}
                          onClick={() => handleImportListingUrl()}
                          className="shrink-0 px-4 py-3 rounded-xl bg-electric/15 border border-electric/40 text-sm hover:bg-electric/25 disabled:opacity-40 inline-flex items-center gap-2"
                        >
                          {importLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                          Import AI-val
                        </button>
                      </div>
                    </div>
                    <details className="text-xs text-slate-500">
                      <summary className="cursor-pointer text-slate-400 hover:text-slate-300">Haladó: kézi beillesztés (HTML / szöveg)</summary>
                      <textarea
                        data-testid="listing-import-source"
                        value={importAdvancedPaste}
                        onChange={(e) => setImportAdvancedPaste(e.target.value)}
                        rows={5}
                        placeholder="Opcionális: Cmd+A szöveg vagy Cmd+U HTML — importnál a link mezővel együtt használjuk."
                        className="mt-2 w-full rounded-xl bg-black/20 border border-white/10 px-3 py-2 text-sm text-slate-300 font-mono text-[12px] resize-y"
                      />
                    </details>
                    <p className="text-xs text-slate-500">
                      A hasznaltauto.hu gyakran blokkolja a szerveres letöltést — ilyenkor a vágólap-segéd ad teljes adatot és képeket. Csak olyan hirdetést importálj, amit másolni szabad.
                    </p>
                  </div>
                  <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 items-end">
                    <div className="flex-1 w-full">
                      <Inp
                        label={vinRequired ? "Alvázszám (VIN) *" : "Alvázszám (VIN)"}
                        testid="listing-vin"
                        value={form.vin || ""}
                        onChange={(v) => set("vin", v.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "").slice(0, 17))}
                        placeholder="17 karakter"
                      />
                      {form.vin && !isValidVin(form.vin) && (
                        <p className="text-xs text-amber-400/90 mt-1">17 érvényes karakter kell (I, O, Q nem lehet).</p>
                      )}
                    </div>
                    <button
                      type="button"
                      data-testid="listing-vin-decode"
                      disabled={vinLoading || !isValidVin(form.vin)}
                      onClick={() => handleDecodeVin()}
                      className="shrink-0 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm hover:border-electric disabled:opacity-40 inline-flex items-center gap-2"
                    >
                      {vinLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Adatok betöltése
                    </button>
                  </div>
                  <Field label="Márka *">
                    <SearchableSelect testid="listing-make" value={form.make} onChange={setMake} options={makes} loading={loadingMakes} placeholder="Gépelj vagy válassz márkát…" emptyText="Nincs ilyen márka a katalógusban" />
                  </Field>
                  <Field label="Modell *">
                    <SearchableSelect testid="listing-model" value={form.model} onChange={setModel} options={models} loading={loadingModels} disabled={!form.make} placeholder={form.make ? "Gépelj vagy válassz modellt…" : "Előbb márka"} emptyText="Nincs ilyen modell ehhez a márkához" />
                  </Field>
                  <Field label="Motor *">
                    <SearchableSelect
                      searchable={false}
                      testid="listing-engine"
                      value={form.engine}
                      onChange={(v) => set("engine", v)}
                      options={engines}
                      loading={loadingEngines}
                      disabled={!form.make || !form.model}
                      placeholder="Motor"
                      emptyText="Nincs motor a listában"
                    />
                  </Field>
                  <Inp label="Hirdetés címe" testid="listing-title" value={form.listing_title || ""} onChange={(v) => set("listing_title", v)} placeholder={defaultListingTitle(form)} />
                  <Inp label="Évjárat *" testid="listing-year" type="number" value={form.year} onChange={(v) => set("year", v)} />
                  <Field label="Forgalomba helyezés hónapja">
                    <SearchableSelect
                      testid="listing-year-month"
                      value={form.year_month || ""}
                      onChange={(v) => set("year_month", v ? Number(v) : null)}
                      options={[{ v: "", l: "—" }, ...MONTH_OPTIONS.map((m) => ({ v: m.v, l: m.l }))]}
                      placeholder="Opcionális"
                      searchable={false}
                    />
                  </Field>
                  <Field label="Állapot">
                    <SearchableSelect testid="listing-condition" value={form.condition || ""} onChange={(v) => set("condition", v)} options={["", ...CONDITIONS]} placeholder="Opcionális" searchable={false} />
                  </Field>
                  <Inp label="Futott km *" testid="listing-mileage" type="number" value={form.mileage} onChange={(v) => set("mileage", v)} />
                  <Field label="Üzemanyag *">
                    <SearchableSelect testid="listing-fuel" value={form.fuel} onChange={(v) => set("fuel", v)} options={FUEL_TYPES} placeholder="Üzemanyag" searchable={false} />
                  </Field>
                  <Field label="Váltó *">
                    <SearchableSelect testid="listing-transmission" value={form.transmission} onChange={(v) => set("transmission", v)} options={TRANSMISSIONS} placeholder="Váltó" searchable={false} />
                  </Field>
                  <Field label="Karosszéria *">
                    <SearchableSelect testid="listing-body" value={form.body_type} onChange={(v) => set("body_type", v)} options={BODY_TYPES} placeholder="Karosszéria" searchable={false} />
                  </Field>
                  <Field label="Szín">
                    <SearchableSelect testid="listing-color" value={form.color} onChange={(v) => set("color", v)} options={COLORS} placeholder="Opcionális" searchable={false} />
                  </Field>
                  <Inp label="Belső készletazonosító" testid="listing-stock-id" value={form.stock_id || ""} onChange={(v) => set("stock_id", v)} placeholder="Csak neked — nem publikus" />
                </ListingFormSection>

                <ListingFormSection title="Ár" testid="listing-section-price">
                  <Inp label="Eladási ár (Ft) *" testid="listing-price" type="number" value={form.price} onChange={(v) => set("price", v)} />
                </ListingFormSection>

                <ListingVehicleFields
                  form={form}
                  set={set}
                  setForm={setForm}
                  trims={trims}
                  loadingTrims={loadingTrims}
                  onTrimSelect={handleTrimSelect}
                  Inp={Inp}
                  Field={Field}
                />

                <ListingFormSection
                  title="Hol található az autó?"
                  hint="A listában város és megye látszik. Az utca/házszám csak a térképhez kell — nem jelenik meg szövegként a listanézetben."
                  testid="listing-section-location"
                >
                  <Field label="Megye *">
                    <SearchableSelect testid="listing-county" value={form.county} onChange={(v) => set("county", v)} options={HUNGARY_COUNTIES} placeholder="Megye" searchable={false} />
                  </Field>
                  <Inp label="Város *" testid="listing-city" value={form.city} onChange={(v) => set("city", v)} placeholder="pl. Veszprém" />
                  <div className="md:col-span-2">
                    <Inp
                      label="Utca, házszám (opcionális, térképhez)"
                      testid="listing-address"
                      value={form.address}
                      onChange={(v) => set("address", v)}
                      placeholder="pl. Haszkovó utca 18 — ha megadod, pontosabb a térkép"
                    />
                  </div>
                </ListingFormSection>

                <ListingFormSection title="Leírás és felszereltség" testid="listing-section-desc">
                  <div className="md:col-span-2">
                    <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">Szöveges leírás</label>
                    <textarea
                      data-testid="listing-description"
                      rows={4}
                      value={form.description}
                      onChange={(e) => set("description", e.target.value)}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm focus:border-electric outline-none resize-none"
                      placeholder="Állapot, szerviz, extrák…"
                    />
                  </div>
                  <div className="md:col-span-2 pt-2">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="text-sm font-medium text-slate-200">Okmányok és felszereltség</span>
                      {featureCount > 0 && (
                        <span className="text-xs text-electric" data-testid="listing-feature-count">{featureCount} kiválasztva</span>
                      )}
                    </div>
                    <CarFeaturePicker selected={form.features} onToggle={toggleFeature} />
                  </div>
                </ListingFormSection>

                <ListingFormSection
                  title="Hirdetés a platformon"
                  hint="Meddig maradjon fent ez a hirdetés, és mennyi kreditet használ naponta — ez nem az autó adata."
                  testid="listing-section-platform"
                  accent
                >
                  <Inp label="Maximum fenn tartás (nap) *" testid="listing-max-days" type="number" value={form.max_days || 30} onChange={(v) => set("max_days", v)} />
                  <div className="md:col-span-2 text-xs text-slate-500 leading-relaxed">
                    Max. 365 nap. Lejárat vagy elfogyott kredit esetén a hirdetés szünetel. Napi fenntartás: <b>{dailyCost} kredit</b>.
                    Admin jóváhagyás után jelenik meg a nyilvános készletben.
                  </div>
                </ListingFormSection>

                {listingRequirements.length > 0 && (
                  <ListingRequirements requirements={listingRequirements} readOnly />
                )}

                <div className="flex flex-col items-end gap-2 pt-2">
                  {step1Blockers.length > 0 && (
                    <p className="text-xs text-amber-400/90 text-right max-w-md" data-testid="listing-step1-blockers">
                      A továbblépéshez: {step1Blockers.join(" · ")}
                    </p>
                  )}
                  <button
                    data-testid="listing-next"
                    disabled={!step1Valid}
                    onClick={() => setStep(2)}
                    title={
                      step1Blockers.length
                        ? `Hiányzik vagy hibás: ${step1Blockers.join(", ")}`
                        : undefined
                    }
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-electric hover:bg-electric-light disabled:opacity-40 text-white font-medium transition-all"
                  >
                    Tovább a képekhez <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-400 mb-4">
                  <b>Galéria fotók</b> – a hirdetés fő képei. Tölts fel több képet különböző szögekből.
                  Az első kép lesz a hirdetés nyitóképe. <b>Kötelező</b>: legalább 1 kép.
                </p>
                <DragDropUpload images={images} setImages={setImages} testidPrefix="listing-main" />

                {/* Kiegészítők — a 360° videó ELŐTT, hogy egyből látszódjon mit kell bekapcsolni */}
                <div data-testid="listing-addons-section" className="mt-8">
                  <h3 className="font-heading text-lg font-medium mb-1">Kiegészítők a hirdetéshez</h3>
                  <p className="text-xs text-slate-500 mb-4">
                    {editingCar
                      ? "A kiválasztott kiegészítő azonnal aktiválódik kreditért. A 360°-hoz kapcsold be a körbeforgatást, majd görgess le a videó felvételhez."
                      : "A kiválasztott kiegészítők krediteje egyszerre, a feltöltési díjjal együtt vonódik le."}
                  </p>
                  {!editingCar && defaultAddonCount > 0 && (
                    <p className="text-xs text-slate-500 mb-4" data-testid="listing-default-addons-hint">
                      {defaultAddonCount} kiegészítő be van kapcsolva a{" "}
                      <button
                        type="button"
                        className="text-electric hover:underline"
                        onClick={() => navigate("/fiok?listingDefaults=1")}
                      >
                        hirdetés beállításokból
                      </button>
                      {" "}— itt autónként kikapcsolhatod.
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(ADDON_META).map(([key, meta]) => {
                      const info = getAddonInfo(key);
                      const cost = Number(oneTimeCfg[key]) || 0;
                      const active = isAddonActive(key);
                      const unavail = !active && config && !isServiceAvailable(config, key);
                      const unavailMsg = unavail ? serviceUnavailableMessage(config, key) : "";
                      const disabled = !active && unavail;
                      const Icon = meta.icon;
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`listing-addon-${key}`}
                          onClick={() => handleAddonClick(key)}
                          disabled={disabled}
                          title={active ? "Kattints a kikapcsoláshoz" : undefined}
                          className={`text-left rounded-2xl p-4 border transition-all ${
                            active
                              ? "border-electric bg-electric/10 hover:bg-electric/15"
                              : unavail
                                ? "border-amber-500/25 bg-amber-500/5 opacity-80 cursor-not-allowed"
                                : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-electric text-white" : "bg-white/[0.05] text-electric"}`}>
                              {active ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" strokeWidth={1.75} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                                {info?.label}
                                <AddonInfoButton addonKey={key} testid={`listing-addon-info-${key}`} />
                                <span className="text-electric text-xs font-mono">
                                  {cost} kr{meta.repeatable ? " / kérés" : ""}
                                </span>
                              </div>
                              <div className="text-[11px] text-slate-400 mt-0.5">{info?.shortDesc}</div>
                              {unavail && (
                                <div className="text-[10px] text-amber-300/90 mt-1">{unavailMsg}</div>
                              )}
                              {active && (
                                <div className="text-[10px] text-emerald-400/90 mt-1">Bekapcsolva — kattints a kikapcsoláshoz</div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    {!editingCar && Object.entries(WEEKLY_ADDON_META).map(([key, meta]) => {
                      const info = getAddonInfo(key);
                      const cost = Number(weeklyCfg[key]) || 0;
                      const active = weeklyAddons[key];
                      const unavail = !active && config && !isServiceAvailable(config, key);
                      const unavailMsg = unavail ? serviceUnavailableMessage(config, key) : "";
                      const Icon = meta.icon;
                      return (
                        <button
                          key={key}
                          type="button"
                          data-testid={`listing-addon-${key}`}
                          onClick={() => toggleWeekly(key)}
                          disabled={unavail}
                          className={`text-left rounded-2xl p-4 border transition-all ${
                            active
                              ? "border-electric bg-electric/10"
                              : unavail
                                ? "border-amber-500/25 bg-amber-500/5 opacity-80 cursor-not-allowed"
                                : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-electric text-white" : "bg-white/[0.05] text-electric"}`}>
                              {active ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" strokeWidth={1.75} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium flex items-center gap-1.5 flex-wrap">
                                {info?.label}
                                <AddonInfoButton addonKey={key} testid={`listing-addon-info-${key}`} />
                                <span className="text-electric text-xs font-mono">{cost} kr / hét</span>
                              </div>
                              <div className="text-[11px] text-slate-400 mt-0.5">{info?.shortDesc}</div>
                              {unavail && (
                                <div className="text-[10px] text-amber-300/90 mt-1">{unavailMsg}</div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {showAiSupportBrief && (
                    <div ref={aiBriefSectionRef} data-testid="listing-ai-support-brief-section">
                      {!isAddonActive("ai_customer_support") && (
                        <p className="text-[11px] text-slate-400 mb-3 px-1 leading-relaxed">
                          Töltsd ki a kérdéseket, majd kattints fent az AI ügyfélszolgálat kiegészítőre az aktiváláshoz
                          (a fiókban is csak ezután kapcsolható be).
                        </p>
                      )}
                      <AiSupportBriefEditor
                        value={aiSupportPrivate}
                        onChange={setAiSupportPrivate}
                        car={{
                          ...(editCar || editingCar || {}),
                          ...pickExtendedPayload(form),
                          make: form.make,
                          model: form.model,
                          year: form.year,
                          price: Number(form.price),
                          mileage: Number(form.mileage),
                          features: form.features || [],
                          description: form.description,
                          county: form.county,
                          city: form.city,
                        }}
                        testidPrefix="listing-ai-support-brief"
                      />
                    </div>
                  )}
                </div>

                {has360Addon ? (
                  <>
                    {!editingCar && <PremiumOnboarding />}
                    <Section360Video
                      value={images360}
                      onChange={setImages360}
                      videoUrl={video360Url}
                      onVideoUrlChange={setVideo360Url}
                    />
                  </>
                ) : (
                  <div
                    data-testid="addon-hint-360"
                    className="mt-6 rounded-2xl p-5 border border-dashed border-white/15 bg-white/[0.02] text-center"
                  >
                    <RotateCw className="w-8 h-8 text-electric/60 mx-auto mb-2" strokeWidth={1.25} />
                    <div className="text-sm text-slate-300">
                      A <b>360° videó felvétel</b> itt jelenik meg, miután fent bekapcsolod a
                      {" "}<b>360° körbeforgatás</b> kiegészítőt ({Number(oneTimeCfg.viewer_360) || 0} kredit).
                    </div>
                  </div>
                )}

                {!editingCar && (
                  <p className="mt-6 text-xs text-slate-500">
                    Az <b>AI háttércsere</b> a hirdetés mentése után automatikusan lefut a feltöltött képeken, ha itt kipipáltad.
                  </p>
                )}
                {!editingCar && addons.ai_photo_bg && (
                  <div data-testid="listing-aibg-preset" className="mt-4 glass-strong rounded-2xl p-4 border border-electric/25">
                    <div className="text-xs uppercase tracking-widest text-electric mb-1">AI háttér</div>
                    <p className="text-[11px] text-slate-400 mb-3">
                      Minden galéria kép erre a beton stúdió háttérre kerül; az AI a spotfényhez igazítja az autó megvilágítását is. A mentés után automatikusan lefut.
                    </p>
                    <AiBgPresetSelector testidPrefix="listing-aibg-preset" />
                  </div>
                )}
                {editingCar && isAddonActive("ai_photo_bg") && (
                  <AiBgRerunSection
                    carId={editCar?.id || editingCar.id}
                    images={images}
                    originalImages={editCar?.original_images || editingCar?.original_images}
                    testidPrefix="listing-aibg-rerun"
                    onSuccess={({ processed, originals, preset }) => {
                      setImages(processed);
                      setEditCar((c) =>
                        c ? { ...c, image: processed[0], images: processed, addon_ai_photo_bg: true } : c,
                      );
                      setAibgCompare({
                        originals,
                        processed,
                        preset,
                        carId: editCar?.id || editingCar.id,
                      });
                    }}
                  />
                )}

                {!editingCar && listingRequirements.length > 0 && (
                  <div className="mt-6">
                    <ListingRequirements
                      requirements={listingRequirements}
                      accepted={acceptedReq}
                      onToggle={toggleReq}
                    />
                    {!requirementsOk && (
                      <p className="text-xs text-amber-300/90 mt-2" data-testid="listing-req-hint">
                        A hirdetés feladásához pipáld ki az összes kötelező pontot.
                      </p>
                    )}
                  </div>
                )}

                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass hover:bg-white/10 font-medium transition-all"
                  >
                    <ArrowLeft className="w-4 h-4" /> Vissza
                  </button>
                  <button
                    data-testid="listing-submit"
                    disabled={saving || insufficient || processingAI || !requirementsOk}
                    onClick={submit}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white font-medium transition-all hover:blue-glow"
                  >
                    {(saving || processingAI) && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingCar
                      ? "Módosítások mentése"
                      : processingAI
                      ? "AI háttércsere folyamatban…"
                      : `Hirdetés feladása (-${totalCost} kredit)`}
                  </button>
                </div>
                {aibgProgress && (
                  <div data-testid="listing-aibg-progress" className="mt-4 glass-strong rounded-2xl p-4 border border-electric/30">
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-electric uppercase tracking-widest">AI háttércsere · {aibgProgress.preset}</span>
                      <span className="text-slate-300">{aibgProgress.done} / {aibgProgress.total} kép</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                      <div
                        className="h-full bg-electric transition-all duration-300"
                        style={{ width: `${Math.round((aibgProgress.done / aibgProgress.total) * 100)}%` }}
                      />
                    </div>
                    <div className="flex justify-end mt-3">
                      <button
                        type="button"
                        data-testid="listing-aibg-cancel"
                        onClick={() => { aibgCancelRef.current = true; }}
                        disabled={aibgCancelRef.current}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-400/30 text-red-300 text-xs disabled:opacity-50"
                      >
                        {aibgCancelRef.current ? "Megszakítás alatt…" : "Megszakítás"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {step === 3 && savedCar && (
              <div data-testid="listing-step3-360">
                <div className="glass-strong rounded-2xl p-4 border border-electric/30 mb-4">
                  <div className="text-xs uppercase tracking-widest text-electric mb-1">Hirdetés feladva ✓</div>
                  <div className="text-sm text-slate-200">
                    Most vedd fel a <b>körbesétálós videót</b> (vagy tölts fel egyet), majd mentsd a hirdetést.
                  </div>
                </div>
                <Section360Video
                  value={images360}
                  onChange={setImages360}
                  videoUrl={video360Url}
                  onVideoUrlChange={setVideo360Url}
                />
                <div className="flex justify-between mt-8">
                  <button
                    onClick={() => navigate("/fiok")}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full glass hover:bg-white/10 font-medium transition-all"
                    data-testid="listing-360-later"
                  >
                    Később töltöm fel
                  </button>
                  <button
                    data-testid="listing-360-save"
                    disabled={saving}
                    onClick={saveThreeSixty}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white font-medium transition-all hover:blue-glow"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    360° mentése
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      <AiBgComparison
        open={!!aibgCompare}
        originals={aibgCompare?.originals || []}
        processed={aibgCompare?.processed || []}
        preset={aibgCompare?.preset}
        retrying={processingAI}
        onAccept={() => setAibgCompare(null)}
        onRevert={async () => {
          if (!aibgCompare) return;
          setProcessingAI(true);
          try {
            const r = await api.post(`/my/cars/${aibgCompare.carId}/ai-bg/revert`);
            setImages(r.data.images || aibgCompare.originals);
            setAibgCompare(null);
            toast.success("Visszaállítva az eredeti képekre");
          } catch (e) {
            toast.error(e.response?.data?.detail || "Visszaállítás sikertelen");
          } finally { setProcessingAI(false); }
        }}
      />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
    {children}
  </div>
);

const Inp = ({ label, testid, value, onChange, type = "text", step, placeholder }) => (
  <div>
    {label ? (
      <label className="text-xs tracking-[0.15em] uppercase text-slate-400 block mb-2">{label}</label>
    ) : null}
    <input
      data-testid={testid}
      type={type}
      step={step}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-white/[0.03] border border-white/10 rounded-xl p-3 text-sm focus:border-electric outline-none transition-colors placeholder:text-slate-500"
    />
  </div>
);
